import { useEffect } from 'react';

const SITE_URL = 'https://iamtoiawallace.com';
const SITE_NAME = 'Toia Wallace, Founder HQ';
const DEFAULT_OG_IMAGE =
  'https://customer-assets.emergentagent.com/job_86d138bb-490b-4c43-8779-6497c2705276/artifacts/9lobmvmy_Professional%20Model%209.png';

function setMeta(selector, attr, value, create) {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    if (!create) return { el: null, created: false, prev: null };
    el = document.createElement('meta');
    Object.entries(create).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, created, prev };
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, created, prev };
}

function setJSONLD(id, data) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  const prev = el.textContent;
  el.textContent = JSON.stringify(data);
  return { el, created, prev };
}

/**
 * Central hook for per-route SEO metadata.
 * Manages: title, description, canonical, robots, Open Graph, Twitter Card, JSON-LD.
 */
export function usePageMeta({
  title,
  description,
  path,
  image,
  robots,
  ogType = 'website',
  jsonLd,
} = {}) {
  useEffect(() => {
    const restores = [];
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      restores.push(setMeta('meta[name="description"]', 'content', description, { name: 'description' }));
      restores.push(setMeta('meta[property="og:description"]', 'content', description, { property: 'og:description' }));
      restores.push(setMeta('meta[name="twitter:description"]', 'content', description, { name: 'twitter:description' }));
    }

    if (title) {
      restores.push(setMeta('meta[property="og:title"]', 'content', title, { property: 'og:title' }));
      restores.push(setMeta('meta[name="twitter:title"]', 'content', title, { name: 'twitter:title' }));
    }

    restores.push(setMeta('meta[property="og:site_name"]', 'content', SITE_NAME, { property: 'og:site_name' }));
    restores.push(setMeta('meta[property="og:type"]', 'content', ogType, { property: 'og:type' }));

    const url = path ? `${SITE_URL}${path}` : (typeof window !== 'undefined' ? window.location.href : SITE_URL);
    restores.push(setMeta('meta[property="og:url"]', 'content', url, { property: 'og:url' }));
    restores.push(setLink('canonical', url));

    const img = image || DEFAULT_OG_IMAGE;
    restores.push(setMeta('meta[property="og:image"]', 'content', img, { property: 'og:image' }));
    restores.push(setMeta('meta[name="twitter:image"]', 'content', img, { name: 'twitter:image' }));
    restores.push(setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image', { name: 'twitter:card' }));

    if (robots) {
      restores.push(setMeta('meta[name="robots"]', 'content', robots, { name: 'robots' }));
    }

    let jsonLdRestore = null;
    if (jsonLd) {
      const key = jsonLd['@type'] || 'page';
      jsonLdRestore = setJSONLD(`page-${key}`, jsonLd);
    }

    return () => {
      document.title = prevTitle;
      restores.forEach((r) => {
        if (!r || !r.el) return;
        if (r.created) r.el.remove();
        else if (r.prev != null) {
          const attr = r.el.hasAttribute('href') ? 'href' : 'content';
          r.el.setAttribute(attr, r.prev);
        }
      });
      if (jsonLdRestore) {
        if (jsonLdRestore.created) jsonLdRestore.el.remove();
        else if (jsonLdRestore.prev != null) jsonLdRestore.el.textContent = jsonLdRestore.prev;
      }
    };
  }, [title, description, path, image, robots, ogType, jsonLd]);
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
